import type { BannerConfig, CouponConfig, PageConfig } from './types.ts';

export function createId(prefix = 'b'): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export function defaultCoupon(overrides: Partial<CouponConfig> = {}): CouponConfig {
    return {
        enabled: false,
        brand: '',
        code: '',
        discount: '',
        eyebrow: 'Cupom exclusivo',
        emptyText: 'Em breve um cupom exclusivo para você',
        ctaLabel: 'Ir para o site',
        siteUrl: '',
        modalBg: '#e8ddd9',
        modalBg2: '#d9c8c4',
        modalText: '#1a1a1a',
        boxBg: '#111111',
        codeColor: '#e2c48a',
        buttonBg: '#111111',
        buttonText: '#ffffff',
        ...overrides,
    };
}

export function createBanner(overrides: Partial<BannerConfig> = {}): BannerConfig {
    return {
        id: createId(),
        title: 'NOVO BANNER',
        titleAlign: 'center',
        fontSize: 28,
        letterSpacing: 2,
        uppercase: true,
        backgroundColor: '#162947',
        textColor: '#ffffff',
        marginTop: 8,
        marginBottom: 0,
        paddingY: 18,
        paddingX: 20,
        borderRadius: 14,
        minHeight: 80,
        borderWidth: 0,
        borderColor: '#ffffff',
        shadow: 'soft',
        href: '',
        coupon: defaultCoupon(),
        ...overrides,
    };
}

export function createExamplePage(): PageConfig {
    return {
        backgroundColor: '#000000',
        backgroundImage: null,
        profileImage: null,
        maxWidth: 600,
        pagePaddingX: 20,
        pagePaddingY: 20,
        bannerGap: 12,
        banners: [
            createBanner({
                id: 'exemplo-whatsapp',
                title: 'WHATSAPP',
                titleAlign: 'center',
                fontSize: 26,
                backgroundColor: '#128C7E',
                href: 'https://wa.me/5511999999999',
            }),
            createBanner({
                id: 'exemplo-instagram',
                title: 'INSTAGRAM',
                titleAlign: 'center',
                fontSize: 26,
                backgroundColor: '#C13584',
                href: 'https://instagram.com/',
            }),
            createBanner({
                id: 'exemplo-cupom',
                title: 'CUPOM 10% OFF',
                titleAlign: 'center',
                fontSize: 24,
                backgroundColor: '#C9A96E',
                href: '',
                coupon: defaultCoupon({
                    enabled: true,
                    brand: 'Minha Loja',
                    code: 'VIBE10',
                    discount: '10% off',
                    eyebrow: 'Cupom exclusivo',
                    ctaLabel: 'Ir para o site',
                    siteUrl: 'https://linksvibe.com.br',
                }),
            }),
        ],
    };
}

export function applyStyleFromBanner(source: BannerConfig, target: BannerConfig): BannerConfig {
    return {
        ...target,
        titleAlign: source.titleAlign,
        fontSize: source.fontSize,
        letterSpacing: source.letterSpacing,
        uppercase: source.uppercase,
        backgroundColor: source.backgroundColor,
        textColor: source.textColor,
        marginTop: source.marginTop,
        marginBottom: source.marginBottom,
        paddingY: source.paddingY,
        paddingX: source.paddingX,
        borderRadius: source.borderRadius,
        minHeight: source.minHeight,
        borderWidth: source.borderWidth,
        borderColor: source.borderColor,
        shadow: source.shadow,
        coupon: {
            ...target.coupon,
            modalBg: source.coupon.modalBg,
            modalBg2: source.coupon.modalBg2,
            modalText: source.coupon.modalText,
            boxBg: source.coupon.boxBg,
            codeColor: source.coupon.codeColor,
            buttonBg: source.coupon.buttonBg,
            buttonText: source.coupon.buttonText,
        },
    };
}

export function shadowCss(shadow: BannerConfig['shadow']): string {
    if (shadow === 'soft') return '0 8px 22px rgba(0, 0, 0, 0.22)';
    if (shadow === 'strong') return '0 14px 32px rgba(0, 0, 0, 0.38)';
    return 'none';
}

export function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

export function maskBrPhone(value: string): string {
    const digits = digitsOnly(value).slice(0, 11);
    if (digits.length === 0) return '';
    if (digits.length < 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function isValidBrLocalPhone(value: string): boolean {
    const length = digitsOnly(value).length;
    return length === 10 || length === 11;
}

export function buildWaMeUrl(localDigits: string, text: string): string {
    const phone = `55${digitsOnly(localDigits)}`;
    const trimmed = text.trim();
    if (!trimmed) return `https://wa.me/${phone}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(trimmed)}`;
}

export function parseWaMeUrl(url: string): { localDigits: string; text: string } | null {
    const raw = url.trim();
    if (!raw) return { localDigits: '', text: '' };
    try {
        const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        const parsed = new URL(withProto);
        const host = parsed.hostname.replace(/^www\./, '');
        let phone = '';
        if (host === 'wa.me') {
            phone = digitsOnly(parsed.pathname);
        } else if (host.endsWith('whatsapp.com')) {
            phone = digitsOnly(parsed.searchParams.get('phone') || '');
        } else {
            return null;
        }
        if (phone.startsWith('55')) phone = phone.slice(2);
        return {
            localDigits: phone.slice(0, 11),
            text: parsed.searchParams.get('text') || '',
        };
    } catch {
        return null;
    }
}

export function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function isSafeColor(value: string): boolean {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value.trim());
}

export function safeColor(value: string, fallback: string): string {
    return isSafeColor(value) ? value.trim() : fallback;
}

export function dataUrlToBlob(dataUrl: string): { blob: Blob; ext: string } {
    const svgMatch = dataUrl.match(/^data:image\/svg\+xml(?:;charset=UTF-8)?,(.*)$/i);
    if (svgMatch && !dataUrl.includes(';base64,')) {
        const svg = decodeURIComponent(svgMatch[1]);
        return { blob: new Blob([svg], { type: 'image/svg+xml' }), ext: 'svg' };
    }

    const match = dataUrl.match(/^data:(.+?);base64,(.+)$/);
    if (!match) {
        throw new Error('Imagem inválida');
    }
    const mime = match[1];
    const b64 = match[2];
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : mime.includes('svg') ? 'svg' : 'jpg';
    return { blob: new Blob([bytes], { type: mime }), ext };
}

export function readAndResizeImage(file: File, maxEdge: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
            const width = Math.max(1, Math.round(img.width * scale));
            const height = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Não foi possível ler a imagem'));
                return;
            }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(objectUrl);
            resolve(canvas.toDataURL('image/jpeg', 0.88));
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Não foi possível ler a imagem'));
        };
        img.src = objectUrl;
    });
}

export const PLACEHOLDER_PROFILE =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="1600" viewBox="0 0 1280 1600">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#1A1A1A"/>
      <stop offset="100%" stop-color="#000000"/>
    </linearGradient>
  </defs>
  <rect width="1280" height="1600" fill="url(#g)"/>
  <text x="640" y="790" text-anchor="middle" font-family="Arial, sans-serif" font-size="64" fill="#C9A96E" letter-spacing="8">EXEMPLO</text>
  <text x="640" y="860" text-anchor="middle" font-family="Arial, sans-serif" font-size="28" fill="#E8EEF8">1280 × 1600</text>
</svg>`);
